import { useContext } from 'react'
import { ScenarioStatesProviderContext } from '../providers/StatesProvider'
import useNavigation from './useNavigation'
import useBaseApi from './useBaseApi'
import useHwilApi from './useHwilApi'
import useFaultApis from './useFaultApis'
import useReportWindow from './useReportWindow'
import useStaticData from './useStaticData'
import useStepper from './useStepper'
import { getInitialFormData } from '../utils/constants'
import { SCENARIO_DEPLOYMENT_TYPES, SCENARIO_OPERATION_TYPES, SCENARIO_STEPS } from '../../constants'
import { ScenarioFormDataType, FaultReportType } from '../utils/scenario-types'
import { ScenarioResponseType } from '../../types'
import { createGroundPacket, createSatellitePacket } from '../utils/viewer-packets'
import { formatOrbitalInsightsData, formatPresimData, getViewerPacketDetails } from '../utils/helperFunction'
import { getOrbitalInsights } from '../../../CommonComponents/orbital-insights/api'
import { OrbitalInsightSatelliteType } from '../../../CommonComponents/orbital-insights/orbital-insights-type'
import { showToastMessage } from '../../../../utils/commonFunctions'

const useSatelliteGroupsAdd = () => {
    const { missionId, scenarioId } = useNavigation()
    const context = useContext(ScenarioStatesProviderContext)
    if (context === undefined) {
        throw new Error('useSatelliteGroupsAdd must be used within a Scenario States Provider')
    }

    const { getScenarioDetails } = useBaseApi()
    const { getHWILComponents } = useHwilApi()
    const { getallFaultReportData } = useFaultApis()
    const { closeAllReportModals } = useReportWindow()
    const { allStations, allFaults, allTaskPlans, allGeoFeatures } = useStaticData()
    const { updateScenarioStep } = useStepper()

    const {
        viewer,
        setFormData,
        setLoading,
        setScenarioResponse,
        setScenarioBaseData,
        setSelectedGroupSatellite,
    } = context

    const setPlottingLoader = (plotting: boolean) => {
        setLoading((prev) => ({
            ...prev,
            execution: plotting ? 'Plotting' : false,
            executionMessage: plotting ? prev.executionMessage : false
        }))
    }

    const failSatelliteGroupsAdd = (message?: string) => {
        setPlottingLoader(false)
        if (message) showToastMessage('error', message)
    }

    const completeSatelliteGroupsAdd = async () => {
        if (!missionId || !scenarioId) return
        try {
            const scenario: ScenarioResponseType = await getScenarioDetails(missionId, scenarioId)
            const selectedGroup = scenario?.satelliteGroups?.[0]
            const selectedSatellite = selectedGroup?.satellites?.[0]?.satelliteId
            if (!selectedGroup || !selectedSatellite) {
                throw new Error('No satellite groups found after plotting completed.')
            }

            const isTrueTwinCommandCenter = scenario.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.TRUETWIN_COMMAND_CENTER
            const INITIAL_FORM_DATA = getInitialFormData()

            let orbitalInsights: Record<string, OrbitalInsightSatelliteType> | null = null
            let faultPlans: { status: "completed" | "idle" | "invalidated"; data: FaultReportType } | null = null

            if (!isTrueTwinCommandCenter) {
                const orbitalInsightsResponse = await getOrbitalInsights(missionId, scenarioId)
                orbitalInsights = formatOrbitalInsightsData(orbitalInsightsResponse)
                faultPlans = await getallFaultReportData()
            }

            if (scenario.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP) {
                const allHwilComponents = await getHWILComponents()
                setScenarioBaseData((prev) => ({
                    ...prev,
                    allHwilComponents
                }))
            }

            setScenarioResponse(scenario)
            const scenarioGroups = (scenario?.satelliteGroups || []).map(gp => gp.satelliteGroupId)
            setFormData((prev) => {
                const data: ScenarioFormDataType = {
                    ...prev,
                    hwil: INITIAL_FORM_DATA.hwil,
                    schedule: {
                        ...prev.schedule,
                        taskplans: prev.schedule.taskplans.filter(tp => scenarioGroups.includes(tp.satelliteGroupId)),
                    },
                    faults: prev.faults.filter(fault => scenarioGroups.includes(fault.satelliteGroupId)),
                    assets: {
                        ...prev?.assets,
                        schedule: INITIAL_FORM_DATA.assets.schedule,
                        faultPlans: isTrueTwinCommandCenter ? prev.assets.faultPlans : (faultPlans ? {
                            ...faultPlans,
                            status: 'invalidated' as const
                        } : prev.assets.faultPlans),
                        presim: {
                            ...prev.assets.presim,
                            data: formatPresimData(prev.assets.presim.data, scenario?.satelliteGroups)
                        },
                        hwilAssets: INITIAL_FORM_DATA.assets.hwilAssets,
                        orbitalInsights: isTrueTwinCommandCenter ? prev.assets.orbitalInsights : {
                            status: orbitalInsights ? 'completed' : 'idle',
                            data: orbitalInsights
                        }
                    }
                }
                const { allGroundStations, selectedStations, selectedTaskplans, selectedFaultPlans } = getViewerPacketDetails(data, allTaskPlans, allGeoFeatures, allFaults, allStations)
                createGroundPacket(viewer, allGroundStations, selectedStations, selectedTaskplans, selectedFaultPlans)
                return data
            })

            setSelectedGroupSatellite({
                groupId: selectedGroup?.satelliteGroupId,
                satelliteId: selectedSatellite
            })

            await createSatellitePacket(viewer, scenarioId, missionId, selectedSatellite)
            const nextStep = scenario?.scenarioDeploymentType === SCENARIO_DEPLOYMENT_TYPES.HARDWARE_IN_LOOP
                ? SCENARIO_STEPS.HWIL
                : scenario?.scenarioOperation === SCENARIO_OPERATION_TYPES.LEOP
                    ? SCENARIO_STEPS.INITIAL_ALTITUDE
                    : SCENARIO_STEPS.SEGMENTS
            closeAllReportModals()
            updateScenarioStep(nextStep)
            setPlottingLoader(false)
        } catch (error: any) {
            failSatelliteGroupsAdd(error?.message || 'Failed to complete satellite plotting. Please try again.')
        }
    }

    return {
        completeSatelliteGroupsAdd,
        failSatelliteGroupsAdd,
        setPlottingLoader,
    }
}

export default useSatelliteGroupsAdd
